import { useState, useCallback, useRef, useEffect } from "react";
import {
  AccountTreeGroup,
  DocumentGlType,
  JournalPickerType,
} from "../../domain/entities/AccountTreeEntity";
import { glMappingRepository } from "../../data/repositories/GLMappingRepositoryImpl";
import {
  mergeTreeGroups,
  setGroupNodes,
} from "../../utils/glTreeUtils";
import { useDropdownCache } from "./useDropdownCache";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

export interface UseAccountNameTreeOptions {
  docType: DocumentGlType;
  journalType?: JournalPickerType;
  selectedAccountId?: string;
  enabled?: boolean;
}

export interface UseAccountNameTreeReturn {
  groups: AccountTreeGroup[];
  loading: boolean;
  loadingMore: boolean;
  loadingTypes: Set<string>;
  hasMore: boolean;
  searchTerm: string;
  searchActive: boolean;
  selectedAccountType: string | null;
  setSearchTerm: (term: string) => void;
  loadAccountType: (accountType: string) => Promise<void>;
  loadMore: () => void;
  error: string | null;
  refetch: () => void;
}

export function useAccountNameTree({
  docType,
  journalType,
  selectedAccountId,
  enabled = true,
}: UseAccountNameTreeOptions): UseAccountNameTreeReturn {
  const { getCached, setCached } = useDropdownCache();
  const [groups, setGroups] = useState<AccountTreeGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState<Set<string>>(new Set());
  const [hasMore, setHasMore] = useState(false);
  const [searchTerm, setSearchTermState] = useState("");
  const [selectedAccountType, setSelectedAccountType] = useState<string | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const offsetRef = useRef(0);
  const debouncedSearchRef = useRef("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoadingMoreRef = useRef(false);
  const loadedTypesRef = useRef<Set<string>>(new Set());

  const searchActive = searchTerm.trim().length > 0;

  const fetchSummary = useCallback(async (): Promise<void> => {
    if (!enabled) return;

    const cacheKey = {
      docType,
      journalType,
      mode: "summary",
      selectedAccountId,
    };
    const cached = getCached<{
      groups: AccountTreeGroup[];
      selectedAccountType: string | null;
    }>("accountTree", cacheKey);
    if (cached) {
      setGroups(cached.groups);
      setSelectedAccountType(cached.selectedAccountType);
      setHasMore(false);
      loadedTypesRef.current = new Set(
        cached.groups
          .filter((g) => g.nodes.length > 0)
          .map((g) => g.account_type),
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await glMappingRepository.fetchAccountTreeSummary(
        docType,
        selectedAccountId,
        journalType,
      );

      if (!result.success) {
        throw new Error("Failed to fetch account tree");
      }

      setGroups(result.groups);
      setSelectedAccountType(result.selectedAccountType);
      setHasMore(false);
      offsetRef.current = 0;

      loadedTypesRef.current = new Set(
        result.groups
          .filter((g) => g.nodes.length > 0)
          .map((g) => g.account_type),
      );

      setCached("accountTree", cacheKey, {
        groups: result.groups,
        selectedAccountType: result.selectedAccountType,
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to load accounts";
      setError(message);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [
    docType,
    enabled,
    getCached,
    journalType,
    selectedAccountId,
    setCached,
  ]);

  const fetchSearchPage = useCallback(
    async (search: string, offset: number, append: boolean): Promise<void> => {
      if (!enabled) return;

      const cacheKey = { docType, journalType, search, offset, mode: "search" };

      if (offset === 0 && !append) {
        const cached = getCached<{
          groups: AccountTreeGroup[];
          hasMore: boolean;
        }>("accountTree", cacheKey);
        if (cached) {
          setGroups(cached.groups);
          setHasMore(cached.hasMore);
          offsetRef.current = offset;
          return;
        }
      }

      if (append) {
        setLoadingMore(true);
        isLoadingMoreRef.current = true;
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const result = await glMappingRepository.fetchAccountTreeSearch(
          docType,
          search,
          PAGE_SIZE,
          offset,
          journalType,
        );

        if (!result.success) {
          throw new Error("Failed to fetch account tree");
        }

        setGroups((prev) =>
          append ? mergeTreeGroups(prev, result.groups) : result.groups,
        );

        const nextHasMore = result.pagination?.has_more ?? false;
        setHasMore(nextHasMore);
        offsetRef.current = offset;

        if (offset === 0 && !append) {
          setCached("accountTree", cacheKey, {
            groups: result.groups,
            hasMore: nextHasMore,
          });
        }
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Failed to load accounts";
        setError(message);
        if (!append) {
          setGroups([]);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
        isLoadingMoreRef.current = false;
      }
    },
    [docType, enabled, getCached, journalType, setCached],
  );

  const loadAccountType = useCallback(
    async (accountType: string): Promise<void> => {
      if (!enabled || searchActive) return;
      if (loadedTypesRef.current.has(accountType)) return;

      const cacheKey = {
        docType,
        journalType,
        accountType,
        selectedAccountId,
        mode: "type",
      };
      const cached = getCached<{ nodes: AccountTreeGroup["nodes"] }>(
        "accountTree",
        cacheKey,
      );
      if (cached) {
        setGroups((prev) => setGroupNodes(prev, accountType, cached.nodes));
        loadedTypesRef.current.add(accountType);
        return;
      }

      setLoadingTypes((prev) => new Set(prev).add(accountType));
      setError(null);

      try {
        const result = await glMappingRepository.fetchAccountTreeForType(
          docType,
          accountType,
          selectedAccountId,
          journalType,
        );

        if (!result.success) {
          throw new Error("Failed to fetch account tree");
        }

        const typeGroup = result.groups.find(
          (g) => g.account_type === accountType,
        );
        const nodes = typeGroup?.nodes ?? [];

        setGroups((prev) => setGroupNodes(prev, accountType, nodes));
        loadedTypesRef.current.add(accountType);

        setCached("accountTree", cacheKey, { nodes });
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Failed to load accounts";
        setError(message);
      } finally {
        setLoadingTypes((prev) => {
          const next = new Set(prev);
          next.delete(accountType);
          return next;
        });
      }
    },
    [
      docType,
      enabled,
      getCached,
      journalType,
      searchActive,
      selectedAccountId,
      setCached,
    ],
  );

  const refetch = useCallback(() => {
    loadedTypesRef.current = new Set();
    offsetRef.current = 0;
    if (debouncedSearchRef.current) {
      fetchSearchPage(debouncedSearchRef.current, 0, false);
    } else {
      fetchSummary();
    }
  }, [fetchSearchPage, fetchSummary]);

  useEffect(() => {
    if (!enabled) return;
    offsetRef.current = 0;
    debouncedSearchRef.current = "";
    loadedTypesRef.current = new Set();
    setSearchTermState("");
    fetchSummary();
  }, [docType, journalType, selectedAccountId, enabled, fetchSummary]);

  const setSearchTerm = useCallback(
    (term: string) => {
      setSearchTermState(term);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        const trimmed = term.trim();
        debouncedSearchRef.current = trimmed;
        offsetRef.current = 0;
        loadedTypesRef.current = new Set();

        if (trimmed) {
          fetchSearchPage(trimmed, 0, false);
        } else {
          setHasMore(false);
          fetchSummary();
        }
      }, SEARCH_DEBOUNCE_MS);
    },
    [fetchSearchPage, fetchSummary],
  );

  const loadMore = useCallback(() => {
    if (
      !searchActive ||
      !hasMore ||
      loading ||
      loadingMore ||
      isLoadingMoreRef.current
    ) {
      return;
    }
    const nextOffset = offsetRef.current + PAGE_SIZE;
    fetchSearchPage(debouncedSearchRef.current, nextOffset, true);
  }, [fetchSearchPage, hasMore, loading, loadingMore, searchActive]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    groups,
    loading,
    loadingMore,
    loadingTypes,
    hasMore,
    searchTerm,
    searchActive,
    selectedAccountType,
    setSearchTerm,
    loadAccountType,
    loadMore,
    error,
    refetch,
  };
}
